import React, { useMemo } from "react";
import {
  VulnerableCompaniesReport,
  VulnerableCompanyRecord,
  CountryVulnerabilityData,
  RegionType,
  SEVERITY_BADGE_CLASSES,
  getRelativeTime
} from "../../types/threatIntelligence";

interface VulnerableCompaniesTableProps {
  report?: VulnerableCompaniesReport | null;
  isLoading?: boolean;
  lastUpdated?: Date | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

// Region display configuration
const REGION_CONFIG: Record<RegionType, { label: string; colorClass: string }> = {
  'Europe': { label: '🌍 Europe', colorClass: 'bg-blue-50 border-blue-200 text-blue-700' },
  'North America': { label: '🌎 North America', colorClass: 'bg-green-50 border-green-200 text-green-700' },
  'APAC': { label: '🌏 Asia-Pacific (APAC)', colorClass: 'bg-purple-50 border-purple-200 text-purple-700' }
};

const REGION_ORDER: RegionType[] = ['Europe', 'North America', 'APAC'];

const RiskTierBadge: React.FC<{ tier: VulnerableCompanyRecord["riskTier"] }> = ({ tier }) => {
  const badgeClass = SEVERITY_BADGE_CLASSES[tier] || SEVERITY_BADGE_CLASSES.medium;
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${badgeClass} uppercase`}>
      {tier}
    </span>
  );
};

// Group countries by region
function groupByRegion(countries: CountryVulnerabilityData[]): Map<RegionType, CountryVulnerabilityData[]> {
  const grouped = new Map<RegionType, CountryVulnerabilityData[]>();
  
  for (const region of REGION_ORDER) {
    grouped.set(region, []);
  }
  
  for (const country of countries) {
    const region = country.region || inferRegion(country.countryCode);
    const existing = grouped.get(region) || [];
    existing.push(country);
    grouped.set(region, existing);
  }
  
  return grouped;
}

// Infer region from country code if not provided
function inferRegion(countryCode: string): RegionType {
  // Europe: 20 countries
  const europeCountries = [
    'GB', 'DE', 'FR', 'NL', 'CH', 'IT', 'ES', 'SE', 'PL', 'BE',
    'IE', 'DK', 'NO', 'FI', 'AT', 'PT', 'CZ', 'RO', 'GR', 'HU'
  ];
  // North America: 3 countries
  const northAmericaCountries = ['US', 'CA', 'MX'];
  // APAC: 16 countries (remaining)
  
  if (europeCountries.includes(countryCode)) return 'Europe';
  if (northAmericaCountries.includes(countryCode)) return 'North America';
  return 'APAC';
}

const VulnerableCompaniesTable: React.FC<VulnerableCompaniesTableProps> = ({
  report,
  isLoading = false,
  lastUpdated,
  onRefresh,
  isRefreshing = false
}) => {
  // Group countries by region
  const countriesByRegion = useMemo(() => {
    if (!report?.countries?.length) return new Map<RegionType, CountryVulnerabilityData[]>();
    return groupByRegion(report.countries);
  }, [report?.countries]);

  // Calculate summary stats
  const stats = useMemo(() => {
    if (!report?.countries?.length) return { totalCountries: 0, totalCompanies: 0 };
    const totalCountries = report.countries.length;
    const totalCompanies = report.countries.reduce((sum, c) => sum + c.companies.length, 0);
    return { totalCountries, totalCompanies };
  }, [report?.countries]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-3 bg-gray-200 rounded w-full"></div>
          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
        </div>
        <div className="h-40 bg-gray-200 rounded mt-4"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-bold text-gray-800">Global Vulnerability Report - Top 5 Most Vulnerable Companies</h2>
          <p className="text-[10px] text-gray-500">
            {stats.totalCountries} countries • {stats.totalCompanies} companies across Europe, North America & APAC
          </p>
          {lastUpdated && (
            <p className="text-[10px] text-gray-400">
              Updated {getRelativeTime(lastUpdated.toISOString())}
            </p>
          )}
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
            title="Refresh report"
          >
            <svg
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        )}
      </div>

      {report?.notes?.length ? (
        <div className="bg-blue-50 border border-blue-100 rounded-md p-2 mb-4">
          <ul className="text-[11px] text-blue-700 space-y-1">
            {report.notes.map((note, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="text-blue-500 mt-0.5">•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!report?.countries?.length ? (
        <div className="text-xs text-gray-500">No vulnerable companies data available.</div>
      ) : (
        <div className="space-y-8">
          {REGION_ORDER.map((region) => {
            const regionCountries = countriesByRegion.get(region) || [];
            if (regionCountries.length === 0) return null;

            const regionConfig = REGION_CONFIG[region];
            const regionCompanyCount = regionCountries.reduce((sum, c) => sum + c.companies.length, 0);

            return (
              <div key={region} className="space-y-4">
                {/* Region Header */}
                <div className={`px-4 py-2 rounded-lg border ${regionConfig.colorClass}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold">{regionConfig.label}</h3>
                    <span className="text-xs opacity-75">
                      {regionCountries.length} countries • {regionCompanyCount} companies
                    </span>
                  </div>
                </div>

                {/* Countries in this region */}
                <div className="space-y-4 pl-2">
                  {regionCountries.map((country) => {
                    const sortedCompanies = [...country.companies]
                      .sort((a, b) => b.riskScore - a.riskScore)
                      .slice(0, 5); // Ensure top 5 only

                    return (
                      <div key={country.countryCode} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                            <h3 className="text-xs font-semibold text-gray-800">
                              {country.country} ({country.countryCode})
                            </h3>
                            <p className="text-[10px] text-gray-500">{country.overallRiskSummary}</p>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-[11px]">
                            <thead className="bg-white">
                              <tr className="text-left text-[10px] font-semibold text-gray-500 border-b border-gray-200">
                                <th className="px-3 py-2">Rank</th>
                                <th className="px-3 py-2">Company</th>
                                <th className="px-3 py-2">Industry</th>
                                <th className="px-3 py-2">HQ</th>
                                <th className="px-3 py-2">Size</th>
                                <th className="px-3 py-2">Risk</th>
                                <th className="px-3 py-2">Threat Insights</th>
                                <th className="px-3 py-2">Security Scan</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {sortedCompanies.map((company, index) => (
                                <tr key={company.companyId} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 text-gray-500">#{index + 1}</td>
                                  <td className="px-3 py-2">
                                    <div className="font-semibold text-gray-800">{company.name}</div>
                                    <div className="text-[10px] text-gray-500">{company.country}</div>
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">{company.industry}</td>
                                  <td className="px-3 py-2 text-gray-700">{company.headquarters}</td>
                                  <td className="px-3 py-2 text-gray-700">{company.size}</td>
                                  <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-semibold text-gray-800">{company.riskScore}</span>
                                      <RiskTierBadge tier={company.riskTier} />
                                    </div>
                                    <div className="text-[10px] text-gray-400">Updated {getRelativeTime(company.lastUpdated)}</div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="text-gray-700 line-clamp-2">{company.threatInsights.summary}</div>
                                    <div className="text-[10px] text-gray-500 mt-1">
                                      <span className="font-semibold">Likely:</span> {company.threatInsights.likelyThreats.join(", ")}
                                    </div>
                                    <div className="text-[10px] text-gray-500">
                                      <span className="font-semibold">Vectors:</span> {company.threatInsights.attackVectors.join(", ")}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="text-gray-700">Score: {company.securityScan.score}</div>
                                    <div className="text-[10px] text-gray-500">
                                      Critical: {company.securityScan.criticalFindings} • High: {company.securityScan.highFindings}
                                    </div>
                                    <div className="text-[10px] text-gray-500">
                                      Services: {company.securityScan.exposedServices.join(", ")}
                                    </div>
                                    <div className="text-[10px] text-gray-400">
                                      Scanned {getRelativeTime(company.securityScan.lastScan)}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VulnerableCompaniesTable;
